/* src/index.css - Dark Theme Nexcom Styles with Modern Fonts */

/* ── Font Import ── MUST be first ── */
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=DM+Sans:ital,wght@0,400;0,500;0,600;1,400&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&display=swap');

/* ── Tailwind Base ── */
@tailwind base;
@tailwind components;
@tailwind utilities;

/* ── CSS Variables ── */
:root {
  --nexcom-gold: #f97316;
  --nexcom-gold-dark: #ea580c;
  --nexcom-gold-muted: rgba(249, 115, 22, 0.08);
  --nexcom-teal: #14b8a6;
  --nexcom-teal-dark: #0d9488;
  --bg-body: #000000;
  --bg-card: rgba(30,30,40,0.6);
  --text-dark: #ffffff;
  --text-muted: #a1a1aa;
  --border-light: rgba(255,255,255,0.07);
  --shadow-sm: 0 1px 3px rgba(0,0,0,0.3);
  --shadow-md: 0 10px 40px rgba(0,0,0,0.5);
  --shadow-lg: 0 20px 60px rgba(0,0,0,0.6);
  --radius: 1.25rem;
  --radius-full: 9999px;
  --transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --font-primary: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-body: 'DM Sans', 'Inter', sans-serif;
  --font-display: 'Plus Jakarta Sans', 'Space Grotesk', 'Inter', sans-serif;
}

/* ── Reset ── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  font-family: var(--font-body);
  background: var(--bg-body);
  color: var(--text-dark);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

/* ── Scrollbar ── */
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: var(--bg-body); }
::-webkit-scrollbar-thumb { background: var(--nexcom-gold); border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: var(--nexcom-gold-dark); }

/* ── Container ── */
.container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
@media (max-width: 768px) { .container { padding: 0 1rem; } }

/* ── Section ── */
.section { 
  padding: 3rem 4rem; 
  background: var(--bg-body);
}
.section-alt { background: #0a0a0a; }
.section-white { background: var(--bg-body); }
.section-surface { background: #0a0a0a; }

@media (max-width: 1024px) { 
  .section { 
    padding: 3rem 2.5rem; 
  } 
}

@media (max-width: 768px) { 
  .section { 
    padding: 2.5rem 1.5rem; 
  } 
}

@media (max-width: 480px) { 
  .section { 
    padding: 2rem 1rem; 
  } 
}

.section-header { margin-bottom: 2.5rem; }
.section-title {
  font-family: var(--font-display);
  font-size: clamp(2rem, 3.5vw, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: var(--text-dark);
}
.section-title .highlight { color: var(--nexcom-gold); }
.section-sub {
  font-size: 1.05rem;
  color: var(--text-muted);
  max-width: 520px;
  margin-top: 0.5rem;
  font-family: var(--font-body);
}
@media (max-width: 480px) {
  .section-title { font-size: 1.6rem; }
  .section-sub { font-size: 0.95rem; }
}
.eyebrow {
  display: inline-block;
  color: var(--nexcom-gold);
  font-family: var(--font-primary);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

/* ── Buttons ── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--nexcom-gold);
  color: #fff;
  padding: 0.7rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  font-family: var(--font-primary);
}
.btn-primary:hover {
  background: var(--nexcom-gold-dark);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(249,115,22,0.3);
}
@media (max-width: 480px) {
  .btn-primary { padding: 0.6rem 1.2rem; font-size: 0.8rem; }
  .btn-secondary { padding: 0.6rem 1.2rem; font-size: 0.8rem; }
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--text-dark);
  padding: 0.7rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 0.9rem;
  border: 2px solid rgba(255,255,255,0.15);
  cursor: pointer;
  transition: all var(--transition);
  font-family: var(--font-primary);
}
.btn-secondary:hover {
  border-color: var(--nexcom-gold);
  color: var(--nexcom-gold);
  transform: translateY(-2px);
}

.btn-secondary-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--text-dark);
  padding: 0.7rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 0.9rem;
  border: 2px solid rgba(255,255,255,0.1);
  cursor: pointer;
  transition: all var(--transition);
  width: 100%;
  font-family: var(--font-primary);
}
.btn-secondary-outline:hover {
  border-color: var(--nexcom-gold);
  color: var(--nexcom-gold);
}
@media (max-width: 480px) {
  .btn-secondary-outline { font-size: 0.8rem; padding: 0.6rem 1.2rem; }
}

.btn-teal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--nexcom-teal);
  color: #fff;
  padding: 0.7rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  width: 100%;
  font-family: var(--font-primary);
}
.btn-teal:hover {
  background: var(--nexcom-teal-dark);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(20,184,166,0.3);
}
@media (max-width: 480px) {
  .btn-teal { font-size: 0.8rem; padding: 0.6rem 1.2rem; }
}

/* ── Logo ── */
.logo {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  gap: 0.5rem;
  text-decoration: none;
}
.logo-image {
  height: 115px;
  width: auto;
  display: block;
  transition: transform var(--transition);
}
.logo:hover .logo-image {
  transform: scale(1.05);
}
.logo-text {
  font-family: 'Space Grotesk', var(--font-display);
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #ffffff;
  transition: color var(--transition);
}
.logo:hover .logo-text {
  color: var(--nexcom-gold);
}
@media (max-width: 992px) {
  .logo-image { height: 40px; }
  .logo-text { font-size: 20px; }
}
@media (max-width: 480px) {
  .logo-image { height: 32px; }
  .logo-text { font-size: 18px; }
}

/* ── Main Nav ── */
.main-nav {
  background: rgba(0,0,0,0.95);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  transition: all var(--transition);
  position: relative;
  z-index: 1000;
}

.main-nav.sticky {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: var(--shadow-sm);
}

.main-nav-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.6rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
@media (max-width: 768px) { .main-nav-inner { padding: 0.5rem 1rem; } }

/* ── Desktop Nav ── */
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  flex: 1;
}
@media (max-width: 992px) { .desktop-nav { display: none; } }

.nav-link {
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  font-family: var(--font-primary);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-muted);
  background: none;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  position: relative;
  overflow: hidden;
  text-decoration: none;
}

.nav-link::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2.5px;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  background: var(--nexcom-gold);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.nav-link:hover::after,
.nav-link.active::after {
  width: 60%;
}

.nav-link:hover { 
  color: var(--nexcom-gold); 
  background: rgba(249,115,22,0.08); 
}

.nav-link.active { 
  color: var(--nexcom-gold); 
  background: rgba(249,115,22,0.08); 
}

/* ── Nav Right ── */
.nav-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  z-index: 1001;
}
@media (max-width: 992px) { .nav-right { display: none; } }

/* ── Language Dropdown Styles ── */
.lang-dropdown-wrapper {
  position: relative;
  display: inline-block;
  z-index: 1001;
}

.lang-dropdown-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: var(--font-primary);
}

.lang-dropdown-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.lang-dropdown-btn.open {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--nexcom-gold);
}

.lang-icon {
  font-size: 14px;
  color: var(--text-muted);
}

.lang-current-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--text-dark);
}

.lang-dropdown-arrow {
  font-size: 8px;
  color: var(--text-muted);
  transition: transform 0.3s ease;
}

.lang-dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.lang-dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 160px;
  background: #1a1a2e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
  padding: 4px;
  z-index: 9999;
  animation: dropdownFade 0.2s ease;
  overflow: visible;
}

.lang-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 8px 12px;
  border-radius: 6px;
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-family: var(--font-primary);
}

.lang-dropdown-item:hover {
  background: rgba(249, 115, 22, 0.1);
  color: var(--nexcom-gold);
}

.lang-dropdown-item.active {
  background: rgba(249, 115, 22, 0.12);
  color: var(--nexcom-gold);
}

.lang-option-label {
  flex: 1;
}

.lang-check {
  color: var(--nexcom-gold);
  font-size: 14px;
}

/* Responsive language dropdown */
@media (max-width: 992px) {
  .lang-dropdown-wrapper {
    display: none;
  }
}

@media (max-width: 768px) {
  .lang-dropdown-btn {
    padding: 3px 8px;
    font-size: 10px;
  }
  
  .lang-icon {
    font-size: 12px;
  }
  
  .lang-current-label {
    font-size: 10px;
  }
}

/* ── Dropdown Styles ── */
.dropdown-wrapper {
  position: relative;
  display: inline-block;
  z-index: 1001;
}

.nav-get-started {
  background: var(--nexcom-gold);
  color: #fff;
  padding: 0.35rem 1.2rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  font-family: var(--font-primary);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.nav-get-started:hover {
  background: var(--nexcom-gold-dark);
  transform: scale(1.02);
}
.nav-get-started.open {
  background: var(--nexcom-gold-dark);
}
@media (max-width: 992px) {
  .nav-get-started { font-size: 0.7rem; padding: 0.25rem 0.8rem; }
}

.dropdown-arrow {
  font-size: 0.55rem;
  transition: transform var(--transition);
}
.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 200px;
  background: #1a1a2e;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: var(--radius);
  box-shadow: 0 20px 60px rgba(0,0,0,0.8);
  padding: 0.5rem;
  z-index: 9999;
  animation: dropdownFade 0.2s ease;
  overflow: visible;
}
@keyframes dropdownFade {
  from { opacity: 0; transform: translateY(-10px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@media (max-width: 480px) {
  .dropdown-menu { min-width: 180px; right: -0.5rem; }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
  color: var(--text-muted);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all var(--transition);
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-family: var(--font-primary);
}
.dropdown-item:hover {
  background: rgba(249,115,22,0.1);
  color: var(--nexcom-gold);
}
.dropdown-item-icon {
  font-size: 1rem;
  color: var(--text-muted);
  transition: color var(--transition);
}
.dropdown-item:hover .dropdown-item-icon {
  color: var(--nexcom-gold);
}

.dropdown-divider {
  height: 1px;
  background: rgba(255,255,255,0.06);
  margin: 0.25rem 0.5rem;
}

.mobile-toggle {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  transition: color var(--transition);
}
.mobile-toggle:hover { color: var(--nexcom-gold); }
@media (max-width: 992px) { .mobile-toggle { display: block; } }
@media (max-width: 480px) { .mobile-toggle { font-size: 1.2rem; } }

/* ── Mobile Menu ── */
.mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}
.mobile-menu {
  background: #0a0a0a;
  width: 85%;
  max-width: 320px;
  height: 100%;
  padding: 1.5rem;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
  border-left: 1px solid rgba(255,255,255,0.06);
}
@media (max-width: 480px) {
  .mobile-menu { padding: 1rem; max-width: 300px; }
}
.mobile-menu-header { display: flex; justify-content: flex-end; margin-bottom: 1.5rem; }
.mobile-menu-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-muted);
  cursor: pointer;
  transition: transform var(--transition);
}
.mobile-menu-close:hover { transform: rotate(90deg); color: var(--nexcom-gold); }
.mobile-menu-links { display: flex; flex-direction: column; gap: 0.5rem; }
.mobile-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
  font-family: var(--font-primary);
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-muted);
  background: none;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  width: 100%;
  text-align: left;
  text-decoration: none;
}
.mobile-link:hover { background: rgba(249,115,22,0.08); color: var(--nexcom-gold); }
.mobile-link.active { background: rgba(249,115,22,0.12); color: var(--nexcom-gold); }
.mobile-link-icon { font-size: 1.1rem; }

.mobile-divider {
  height: 1px;
  background: rgba(255,255,255,0.06);
  margin: 0.5rem 0;
}

/* ── Mobile Language Section ── */
.mobile-language-section {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.mobile-language-label {
  display: block;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
}

.mobile-language-buttons {
  display: flex;
  gap: 8px;
}

.mobile-lang-btn {
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: var(--font-primary);
}

.mobile-lang-btn.active {
  background: rgba(20, 184, 166, 0.15);
  border-color: #14b8a6;
  color: #ffffff;
}

.mobile-lang-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* ── Hero ── */
.hero {
  position: relative;
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 40px 0 30px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.85)),
              url('https://thetreasureblog.wordpress.com/wp-content/uploads/2013/05/dsc8343.jpg') center/cover no-repeat fixed;
  z-index: 1;
}

.hero h1 {
  font-family: 'Space Grotesk', var(--font-display);
  font-size: clamp(2.8rem, 6.5vw, 4.5rem);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1.15;
  color: #fff;
  padding-bottom: 0.05em;
}

.hero h1 .highlight {
  font-family: 'Space Grotesk', var(--font-display);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #ffffff;
  background: none;
  -webkit-background-clip: unset;
  -webkit-text-fill-color: unset;
  display: inline-block;
  line-height: 1.2;
  padding-bottom: 0.05em;
  overflow: visible;
}

@media (max-width: 1024px) { .hero { min-height: 80vh; } }
@media (max-width: 768px) { 
  .hero { 
    min-height: 70vh; 
    padding: 60px 0 30px;
    background-attachment: scroll;
  } 
}
@media (max-width: 480px) { 
  .hero { 
    min-height: 60vh; 
    padding: 50px 0 20px;
  }
  .hero h1 {
    font-size: 2.2rem;
    line-height: 1.1;
  }
  .hero h1 .highlight { font-size: 2.2rem; }
}

.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}
.bubble {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(249,115,22,0.05), rgba(249,115,22,0.01));
  border: 1px solid rgba(249,115,22,0.03);
  backdrop-filter: blur(2px);
}
.particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(249,115,22,0.05);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  text-align: center;
  margin: 0 auto;
  padding: 0 1rem;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255,255,255,0.06);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.06);
  padding: 0.3rem 1.2rem;
  border-radius: var(--radius-full);
  font-family: var(--font-primary);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nexcom-gold);
  margin-bottom: 1.5rem;
}
@media (max-width: 480px) {
  .hero-badge { font-size: 0.6rem; padding: 0.2rem 0.8rem; }
}
.hero-badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--nexcom-teal);
  animation: pulse-dot 2s infinite;
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}

.hero p {
  font-family: var(--font-body);
  font-size: 1.1rem;
  color: rgba(255,255,255,0.7);
  max-width: 600px;
  margin: 1rem auto 0;
  line-height: 1.8;
  font-weight: 300;
}
@media (max-width: 480px) {
  .hero p { font-size: 0.95rem; margin: 0.5rem auto 0; }
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
}
@media (max-width: 480px) {
  .hero-actions { gap: 0.75rem; margin-top: 1.5rem; }
}

/* ── Service Selector ── */
.service-select {
  background: #0a0a0a;
  border-top: 1px solid rgba(255,255,255,0.06);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.service-tabs {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}
.service-tab {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 30px 40px;
  border-right: 1px solid rgba(255,255,255,0.06);
  text-decoration: none;
  transition: background 0.2s;
  position: relative;
  background: transparent;
  border-left: none;
  border-top: none;
  border-bottom: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
}
.service-tab:last-child { border-right: none; }
.service-tab::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--nexcom-gold);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.22s ease;
}
.service-tab:hover { 
  background: rgba(255,255,255,0.03); 
}
.service-tab:hover::after { 
  transform: scaleX(1); 
}
.tab-label {
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 7px;
}
.tab-title {
  font-family: var(--font-primary);
  font-size: 19px;
  font-weight: 800;
  color: var(--text-dark);
  letter-spacing: -0.02em;
  margin-bottom: 5px;
}
.tab-desc {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.5;
}

@media (max-width: 992px) {
  .service-tab { padding: 24px 28px; }
  .tab-title { font-size: 17px; }
  .tab-desc { font-size: 12px; }
}
@media (max-width: 768px) {
  .service-tabs { grid-template-columns: 1fr; }
  .service-tab {
    border-right: none;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    padding: 18px 24px;
  }
  .service-tab:last-child { border-bottom: none; }
  .tab-title { font-size: 16px; }
}
@media (max-width: 480px) {
  .service-tab { padding: 14px 18px; }
  .tab-title { font-size: 15px; }
  .tab-desc { font-size: 11px; }
}

/* ── Plans Grid ── */
.plans-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  margin-top: 52px;
}
@media (max-width: 1024px) { .plans-grid { gap: 16px; } }
@media (max-width: 960px) { .plans-grid { grid-template-columns: 1fr 1fr; gap: 16px; } }
@media (max-width: 600px) { 
  .plans-grid { 
    grid-template-columns: 1fr; 
    gap: 16px;
    margin-top: 32px;
  } 
}

.plan-card {
  background: rgba(30,30,40,0.6);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255,255,255,0.06);
  border-radius: 20px;
  padding: 40px 36px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all var(--transition);
}
.plan-card:hover {
  border-color: rgba(249,115,22,0.3);
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(249,115,22,0.05);
}
.plan-card.featured {
  border-color: rgba(249,115,22,0.3);
  background: rgba(30,30,40,0.8);
}
.plan-card.cta-card {
  background: rgba(30,30,40,0.8);
  border-color: rgba(249,115,22,0.3);
  justify-content: center;
  align-items: center;
  text-align: center;
}
@media (max-width: 600px) {
  .plan-card { padding: 24px 20px; }
}
@media (max-width: 480px) {
  .plan-card { padding: 20px 16px; border-radius: 16px; }
}

.plan-badge {
  position: absolute;
  top: 20px;
  right: -1px;
  background: var(--nexcom-gold);
  color: #000;
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 4px 14px 4px 12px;
  border-radius: 100px 0 0 100px;
}
@media (max-width: 480px) {
  .plan-badge { font-size: 8px; padding: 3px 10px 3px 8px; top: 12px; }
}

.plan-name { 
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nexcom-gold);
  margin-bottom: 16px;
}
@media (max-width: 480px) {
  .plan-name { font-size: 10px; margin-bottom: 12px; }
}

.plan-speed {
  font-family: var(--font-display);
  font-size: clamp(52px, 6vw, 70px);
  font-weight: 800;
  color: #5a5a5d;
  letter-spacing: -0.04em;
  line-height: 1;
  margin-bottom: 4px;
  position: relative;
  display: inline-block;
  transition: all 0.3s ease;
}

.plan-speed .speed-number {
  background: linear-gradient(135deg, #7f7d83 0%, #a39fa6 40%, #97949b 70%, #a29fa5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
  animation: speedPulse 3s ease-in-out infinite;
  filter: drop-shadow(0 0 30px rgba(249, 115, 22, 0.15));
}

.plan-speed .speed-unit {
  font-family: var(--font-primary);
  font-size: 19px;
  font-weight: 600;
  color: var(--text-muted);
  vertical-align: middle;
  margin-left: 4px;
  -webkit-text-fill-color: var(--text-muted);
  background: none;
}

@keyframes speedPulse {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 20px rgba(249, 115, 22, 0.1));
  }
  50% {
    transform: scale(1.02);
    filter: drop-shadow(0 0 40px rgba(249, 115, 22, 0.25));
  }
}

.plan-speed::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  background: radial-gradient(ellipse at center, rgba(249, 115, 22, 0.05) 0%, transparent 70%);
  border-radius: 50%;
  z-index: -1;
  animation: glowPulse 3s ease-in-out infinite;
}

@keyframes glowPulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 1;
  }
}

.plan-card:hover .plan-speed .speed-number {
  animation: speedPulseHover 0.8s ease-in-out infinite;
}

@keyframes speedPulseHover {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 30px rgba(249, 115, 22, 0.2));
  }
  50% {
    transform: scale(1.05);
    filter: drop-shadow(0 0 60px rgba(249, 115, 22, 0.4));
  }
}
@media (max-width: 600px) {
  .plan-speed { font-size: clamp(40px, 8vw, 52px); }
  .plan-speed .speed-unit { font-size: 16px; }
}
@media (max-width: 480px) {
  .plan-speed { font-size: clamp(32px, 10vw, 42px); }
  .plan-speed .speed-unit { font-size: 14px; }
}

.plan-price-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 18px 0;
  margin: 18px 0;
  border-top: 1px solid rgba(255,255,255,0.06);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.plan-price-curr {
  font-family: var(--font-primary);
  font-size: 17px;
  font-weight: 600;
  color: var(--text-muted);
}
.plan-price {
  font-family: var(--font-display);
  font-size: 42px;
  font-weight: 800;
  color: var(--text-dark);
  letter-spacing: -0.03em;
  line-height: 1;
}
.plan-price-period {
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--text-muted);
  margin-left: 2px;
}
@media (max-width: 600px) {
  .plan-price { font-size: 34px; }
  .plan-price-curr { font-size: 15px; }
  .plan-price-period { font-size: 13px; }
}
@media (max-width: 480px) {
  .plan-price { font-size: 28px; }
  .plan-price-curr { font-size: 13px; }
  .plan-price-period { font-size: 12px; }
  .plan-price-row { padding: 12px 0; margin: 12px 0; }
}

.plan-features {
  list-style: none;
  margin-bottom: 30px;
  flex: 1;
}
.plan-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--text-muted);
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.plan-features li:last-child { border-bottom: none; }
.plan-features .feat-check {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(20,184,166,0.15);
  flex-shrink: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.plan-features .feat-check svg {
  width: 10px;
  height: 10px;
}
@media (max-width: 480px) {
  .plan-features li { font-size: 12px; padding: 5px 0; }
  .plan-features .feat-check { width: 16px; height: 16px; }
}

/* ── CTA CARD ── */
.cta-emoji { font-size: 2.5rem; margin-bottom: 0.4rem; }
.cta-title { 
  font-family: var(--font-display);
  font-size: 1.1rem; 
  font-weight: 700; 
  margin-bottom: 0.3rem; 
  color: var(--text-dark);
}
.cta-desc {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--text-muted);
  max-width: 260px;
  margin-bottom: 1rem;
}
@media (max-width: 480px) {
  .cta-emoji { font-size: 2rem; }
  .cta-title { font-size: 1rem; }
  .cta-desc { font-size: 0.8rem; }
}

/* ── Enterprise Single Card ── */
.biz-enterprise-wrapper {
  max-width: 100%;
  margin: 0 auto;
  margin-top: 52px;
}

.biz-enterprise-card {
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(249, 115, 22, 0.2);
  border-radius: 24px;
  padding: 3rem 3.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 8px 40px rgba(249, 115, 22, 0.05);
}

.biz-enterprise-card:hover {
  border-color: rgba(249, 115, 22, 0.4);
  box-shadow: 0 12px 60px rgba(249, 115, 22, 0.1);
  transform: translateY(-4px);
}

.enterprise-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 3rem;
  align-items: start;
}

.enterprise-left {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.enterprise-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: rgba(249, 115, 22, 0.12);
  border: 1px solid rgba(249, 115, 22, 0.2);
  padding: 0.4rem 1.2rem;
  border-radius: 9999px;
  width: fit-content;
  font-family: var(--font-primary);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nexcom-gold);
}

.enterprise-badge-icon {
  font-size: 0.85rem;
}

.enterprise-speed {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.enterprise-speed .speed-number.custom-speed {
  font-family: var(--font-display);
  font-size: clamp(48px, 5vw, 64px);
  font-weight: 800;
  background: linear-gradient(135deg, #f97316 0%, #f97316 40%, #ff8a4a 70%, #f97316 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: speedPulse 3s ease-in-out infinite;
  filter: drop-shadow(0 0 30px rgba(249, 115, 22, 0.15));
  line-height: 1;
}

.enterprise-speed .speed-label {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--text-muted);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.enterprise-price-row {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.enterprise-price {
  font-family: var(--font-display);
  font-size: clamp(36px, 4vw, 48px);
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.03em;
  line-height: 1;
}

.enterprise-price-sub {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--text-muted);
}

.enterprise-right {
  display: flex;
  align-items: center;
}

.enterprise-features {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
  width: 100%;
}

.enterprise-features li {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0;
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.enterprise-features li .feat-check {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(20, 184, 166, 0.15);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.enterprise-features li .feat-check svg {
  width: 10px;
  height: 10px;
}

.enterprise-actions {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.enterprise-actions .btn-primary {
  padding: 0.8rem 3rem;
  font-size: 1rem;
  gap: 0.75rem;
}

.enterprise-note {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: var(--text-muted);
  text-align: center;
}

.enterprise-note-link {
  background: none;
  border: none;
  color: var(--nexcom-gold);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  font-family: var(--font-body);
  font-size: 0.8rem;
  padding: 0;
  margin-left: 0.3rem;
  transition: color 0.3s ease;
}

.enterprise-note-link:hover {
  color: var(--nexcom-gold-dark);
}

/* ── Responsive Enterprise ── */
@media (max-width: 992px) {
  .biz-enterprise-card {
    padding: 2rem 2rem;
  }
  
  .enterprise-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .enterprise-features {
    grid-template-columns: 1fr;
  }
  
  .enterprise-price {
    font-size: clamp(32px, 5vw, 40px);
  }
  
  .enterprise-speed .speed-number.custom-speed {
    font-size: clamp(40px, 6vw, 52px);
  }
}

@media (max-width: 768px) {
  .biz-enterprise-wrapper {
    margin-top: 32px;
  }
  
  .biz-enterprise-card {
    padding: 1.5rem 1.5rem;
    border-radius: 20px;
  }
  
  .enterprise-features li {
    font-size: 0.8rem;
    padding: 0.4rem 0;
  }
  
  .enterprise-actions .btn-primary {
    padding: 0.6rem 2rem;
    font-size: 0.9rem;
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .biz-enterprise-card {
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
  
  .enterprise-badge {
    font-size: 0.6rem;
    padding: 0.3rem 0.8rem;
  }
  
  .enterprise-speed .speed-number.custom-speed {
    font-size: clamp(32px, 8vw, 38px);
  }
  
  .enterprise-price {
    font-size: clamp(28px, 6vw, 34px);
  }
  
  .enterprise-price-sub {
    font-size: 0.75rem;
  }
  
  .enterprise-features li {
    font-size: 0.75rem;
    padding: 0.3rem 0;
  }
  
  .enterprise-features li .feat-check {
    width: 16px;
    height: 16px;
  }
  
  .enterprise-features li .feat-check svg {
    width: 8px;
    height: 8px;
  }
  
  .enterprise-note {
    font-size: 0.7rem;
  }
  
  .enterprise-note-link {
    font-size: 0.7rem;
  }
}

/* ── WiFi ── */
.wifi-full-width {
  max-width: 100%;
  margin: 0 auto;
}

.wifi-card-full {
  background: rgba(30,30,40,0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem 3rem;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 8px 32px rgba(0,0,0,0.06);
  width: 100%;
  transition: all 0.3s ease;
}

.wifi-card-full:hover {
  border-color: rgba(249, 115, 22, 0.2);
  box-shadow: 0 12px 48px rgba(249, 115, 22, 0.06);
}

.wifi-card-full .wifi-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.wifi-card-full .wifi-card-header .wifi-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  background: rgba(20,184,166,0.15);
  color: var(--nexcom-teal);
}

.wifi-card-full .wifi-card-header h3 {
  font-size: 1.5rem;
  font-family: var(--font-display);
  color: var(--text-dark);
  margin: 0;
}

.wifi-card-full .wifi-desc {
  font-family: var(--font-body);
  font-size: 0.95rem;
  color: var(--text-muted);
  margin-bottom: 1.5rem;
}

.wifi-pass-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 1.5rem;
}

.wifi-pass-table thead th {
  background: rgba(255,255,255,0.03);
  color: var(--text-dark);
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 2px solid rgba(255,255,255,0.06);
}

.wifi-pass-table thead th:last-child {
  text-align: right;
}

.wifi-pass-table tbody td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  font-family: var(--font-body);
  font-size: 0.95rem;
  color: var(--text-muted);
}

.wifi-pass-table tbody td:first-child {
  font-weight: 600;
  color: var(--text-dark);
}

.wifi-pass-table tbody td:last-child {
  text-align: right;
  font-weight: 700;
  color: var(--nexcom-gold);
  font-size: 1rem;
}

.wifi-pass-table tbody tr:hover {
  background: rgba(255,255,255,0.02);
}

.wifi-pass-table tbody tr:last-child td {
  border-bottom: none;
}

.wifi-card-full .btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--nexcom-gold);
  color: #fff;
  padding: 0.7rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  font-family: var(--font-primary);
  width: 100%;
  margin-bottom: 0.75rem;
}

.wifi-card-full .btn-primary:hover {
  background: var(--nexcom-gold-dark);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(249,115,22,0.3);
}

/* ── Happy Hours Link ── */
.wifi-avail.happy-hours-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 0.8rem;
  margin-top: 0.6rem;
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.08), rgba(20, 184, 166, 0.05));
  border-radius: 10px;
  border: 1px solid rgba(249, 115, 22, 0.12);
  text-decoration: none;
  color: var(--text-muted);
  font-size: 0.8rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.wifi-avail.happy-hours-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.08), transparent);
  transition: left 0.6s ease;
}

.wifi-avail.happy-hours-link:hover::before {
  left: 100%;
}

.wifi-avail.happy-hours-link:hover {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(20, 184, 166, 0.1));
  border-color: var(--nexcom-gold);
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(249, 115, 22, 0.1);
  color: var(--text-dark);
}

.wifi-avail.happy-hours-link .happy-icon {
  color: #f59e0b;
  font-size: 0.85rem;
  animation: happyPulse 2s ease-in-out infinite;
}

@keyframes happyPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.15);
    opacity: 1;
  }
}

.wifi-avail.happy-hours-link span {
  color: var(--text-muted);
  transition: color 0.3s ease;
}

.wifi-avail.happy-hours-link:hover span {
  color: var(--text-dark);
}

.wifi-avail.happy-hours-link span strong {
  color: var(--nexcom-gold);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #f97316, #f59e0b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: all 0.3s ease;
}

.wifi-avail.happy-hours-link:hover span strong {
  background: linear-gradient(135deg, #f59e0b, #f97316);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.wifi-avail.happy-hours-link .external-icon {
  font-size: 0.65rem;
  color: var(--text-muted);
  opacity: 0.5;
  transition: all 0.3s ease;
}

.wifi-avail.happy-hours-link:hover .external-icon {
  color: var(--nexcom-gold);
  opacity: 1;
  transform: translateX(3px) rotate(-5deg);
}

/* ── Responsive WiFi ── */
@media (max-width: 768px) {
  .wifi-card-full {
    padding: 1.5rem 1.5rem;
  }
  
  .wifi-card-full .wifi-card-header h3 {
    font-size: 1.3rem;
  }
  
  .wifi-pass-table thead th {
    font-size: 0.7rem;
    padding: 0.5rem 0.75rem;
  }
  
  .wifi-pass-table tbody td {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
  }
}

@media (max-width: 480px) {
  .wifi-card-full {
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
  
  .wifi-card-full .wifi-card-header {
    gap: 0.5rem;
  }
  
  .wifi-card-full .wifi-card-header .wifi-icon {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
  }
  
  .wifi-card-full .wifi-card-header h3 {
    font-size: 1.1rem;
  }
  
  .wifi-card-full .wifi-desc {
    font-size: 0.85rem;
  }
  
  .wifi-pass-table thead th {
    font-size: 0.6rem;
    padding: 0.4rem 0.5rem;
  }
  
  .wifi-pass-table tbody td {
    font-size: 0.75rem;
    padding: 0.4rem 0.5rem;
  }
  
  .wifi-pass-table tbody td:last-child {
    font-size: 0.85rem;
  }
  
  .wifi-card-full .btn-primary {
    font-size: 0.8rem;
    padding: 0.6rem 1.2rem;
  }
  
  .wifi-avail.happy-hours-link {
    font-size: 0.65rem;
    padding: 0.4rem 0.6rem;
    flex-wrap: wrap;
  }
  
  .wifi-avail.happy-hours-link .happy-icon {
    font-size: 0.7rem;
  }
  
  .wifi-avail.happy-hours-link .external-icon {
    font-size: 0.5rem;
  }
}

/* ── Devices Section ── */
.devices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.device-card {
  background: rgba(30,30,40,0.6);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  border: 1px solid rgba(255,255,255,0.06);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
}

.device-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.08);
  border-color: rgba(20,184,166,0.3);
}

.device-icon {
  font-size: 2.5rem;
  color: var(--nexcom-teal);
  margin-bottom: 1rem;
  background: rgba(20,184,166,0.1);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.device-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  font-family: var(--font-display);
}

.device-desc {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
  line-height: 1.5;
  font-family: var(--font-body);
}

.device-price {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: rgba(255,255,255,0.03);
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.04);
}

.device-features {
  list-style: none;
  padding: 0;
  margin: 1rem 0 1.5rem;
  text-align: left;
}

.device-features li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: var(--text-muted);
  padding: 0.4rem 0;
  font-family: var(--font-body);
  border-bottom: 1px solid rgba(255,255,255,0.03);
}

.device-features li:last-child {
  border-bottom: none;
}

.device-features .feat-check {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.device-features .feat-check svg {
  width: 12px;
  height: 12px;
}

@media (max-width: 768px) {
  .devices-grid {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: 480px) {
  .device-card {
    padding: 1.5rem 1rem;
  }
  
  .device-icon {
    width: 56px;
    height: 56px;
    font-size: 2rem;
  }
  
  .device-name {
    font-size: 1rem;
  }
  
  .device-price {
    font-size: 1.1rem;
  }
  
  .device-features li {
    font-size: 0.75rem;
  }
}

/* ── Testimonials ── */
.testimonials-header {
  text-align: center;
  margin-bottom: 3rem;
}
.testimonials-header .section-sub {
  margin: 0.5rem auto 0;
  max-width: 400px;
}
@media (max-width: 480px) {
  .testimonials-header { margin-bottom: 2rem; }
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}
@media (max-width: 768px) {
  .testimonials-grid { grid-template-columns: 1fr; gap: 1.5rem; }
}
@media (max-width: 480px) {
  .testimonials-grid { gap: 1.2rem; }
}

.testimonial-card {
  background: rgba(30, 30, 40, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius);
  padding: 2rem;
  transition: all var(--transition);
  position: relative;
  overflow: hidden;
}
.testimonial-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--nexcom-gold), var(--nexcom-gold-dark));
  opacity: 0;
  transition: opacity var(--transition);
}
.testimonial-card:hover::before {
  opacity: 1;
}
.testimonial-card:hover {
  transform: translateY(-8px);
  border-color: rgba(249, 115, 22, 0.2);
  box-shadow: 0 20px 60px rgba(249, 115, 22, 0.08);
}
@media (max-width: 768px) {
  .testimonial-card { padding: 1.5rem; }
}
@media (max-width: 480px) {
  .testimonial-card { padding: 1.25rem; }
}

.testimonial-stars {
  display: flex;
  gap: 0.3rem;
  margin-bottom: 1rem;
}
.testimonial-star {
  font-size: 0.9rem;
  color: #f59e0b;
}
@media (max-width: 480px) {
  .testimonial-star { font-size: 0.8rem; }
}

.testimonial-text {
  font-family: var(--font-body);
  font-size: 0.95rem;
  color: var(--text-muted);
  font-style: italic;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  min-height: 80px;
}
@media (max-width: 768px) {
  .testimonial-text { font-size: 0.9rem; min-height: auto; }
}
@media (max-width: 480px) {
  .testimonial-text { font-size: 0.85rem; }
}

.testimonial-name {
  font-family: var(--font-primary);
  font-weight: 700;
  color: var(--text-dark);
  font-size: 0.95rem;
  margin: 0;
}
.testimonial-location {
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: var(--text-muted);
  margin: 0;
}
@media (max-width: 480px) {
  .testimonial-name { font-size: 0.85rem; }
  .testimonial-location { font-size: 0.7rem; }
}

/* ── Coverage Section ── */
.coverage-section {
  background: #0a0a0a;
  color: #fff;
  padding: 4rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.coverage-section::before {
  content: '';
  position: absolute;
  top: -50%;
  left: 50%;
  transform: translateX(-50%);
  width: 800px;
  height: 400px;
  background: radial-gradient(ellipse at center, rgba(249,115,22,0.03), transparent 70%);
  pointer-events: none;
}
.coverage-title {
  font-family: var(--font-display);
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700;
  position: relative;
}
.coverage-desc {
  font-family: var(--font-body);
  color: rgba(255,255,255,0.6);
  max-width: 460px;
  margin: 0.5rem auto 1.5rem;
  position: relative;
  font-size: 1rem;
}
.coverage-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  position: relative;
}
.coverage-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: #000;
  padding: 0.7rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  font-family: var(--font-primary);
}
.coverage-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(255,255,255,0.15);
}
.coverage-btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: #fff;
  padding: 0.7rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 0.9rem;
  border: 2px solid rgba(255,255,255,0.15);
  cursor: pointer;
  transition: all var(--transition);
  text-decoration: none;
  font-family: var(--font-primary);
}
.coverage-btn-secondary:hover {
  border-color: rgba(255,255,255,0.3);
  transform: translateY(-2px);
}
.coverage-contacts {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 1.5rem;
  position: relative;
}
.coverage-contacts a {
  font-family: var(--font-body);
  color: rgba(255,255,255,0.5);
  text-decoration: none;
  font-size: 0.85rem;
  transition: color var(--transition);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.coverage-contacts a:hover { color: var(--nexcom-gold); }
@media (max-width: 768px) {
  .coverage-section { padding: 3rem 1.5rem; }
}
@media (max-width: 480px) {
  .coverage-section { padding: 2rem 1rem; }
  .coverage-btn-primary, .coverage-btn-secondary { padding: 0.6rem 1.2rem; font-size: 0.8rem; }
  .coverage-contacts a { font-size: 0.75rem; }
  .coverage-contacts { gap: 1rem; }
}

/* ── Locations Grid ── */
.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin: 2rem auto;
  max-width: 900px;
  padding: 0.5rem;
}

.location-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(30, 30, 40, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all var(--transition);
  font-weight: 500;
  color: var(--text-dark);
  font-family: var(--font-body);
  font-size: 0.9rem;
}

.location-item:hover {
  border-color: rgba(249, 115, 22, 0.3);
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.1);
}

.location-icon {
  color: var(--nexcom-gold);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .locations-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }
  .location-item {
    padding: 0.6rem 0.8rem;
    font-size: 0.8rem;
  }
}
@media (max-width: 480px) {
  .locations-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
  }
  .location-item {
    padding: 0.5rem 0.6rem;
    font-size: 0.75rem;
    border-radius: 8px;
  }
  .location-icon { font-size: 0.75rem; }
}

/* ── Footer ── */
.footer {
  background: #0a0a0a;
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 3rem 0 1.5rem;
}
.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}
@media (max-width: 1024px) { 
  .footer-grid { grid-template-columns: 1fr 1fr 1fr; gap: 1.5rem; } 
}
@media (max-width: 768px) { 
  .footer-grid { grid-template-columns: 1fr 1fr; gap: 1.5rem; } 
}
@media (max-width: 600px) { 
  .footer-grid { grid-template-columns: 1fr; gap: 1.2rem; } 
}

.footer-brand p {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.7;
  margin-top: 0.3rem;
}
@media (max-width: 480px) {
  .footer-brand p { font-size: 0.75rem; }
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
}
.footer-logo-image {
  height: 115px;
  width: auto;
  display: block;
}
.footer-logo-text {
  font-family: 'Space Grotesk', var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #ffffff;
  transition: color var(--transition);
}
.footer-logo-text:hover {
  color: var(--nexcom-gold);
}
@media (max-width: 768px) {
  .footer-logo-image { height: 38px; }
  .footer-logo-text { font-size: 20px; }
}
@media (max-width: 480px) {
  .footer-logo-image { height: 32px; }
  .footer-logo-text { font-size: 18px; }
}

.footer-socials {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.footer-social {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: all var(--transition);
  text-decoration: none;
  font-size: 0.85rem;
}
.footer-social:hover {
  border-color: var(--nexcom-gold);
  color: var(--nexcom-gold);
  transform: translateY(-3px);
  box-shadow: var(--shadow-sm);
}
@media (max-width: 480px) {
  .footer-social { width: 30px; height: 30px; font-size: 0.75rem; }
}

.footer-col h4 {
  font-family: var(--font-primary);
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #5a5a5d;
  margin-bottom: 0.75rem;
  position: relative;
  display: inline-block;
}
.footer-col h4::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--nexcom-gold);
  transition: width var(--transition);
}
.footer-col:hover h4::after { width: 100%; }
.footer-col ul { list-style: none; }
.footer-col ul li { margin-bottom: 0.3rem; }
.footer-link {
  font-family: var(--font-body);
  color: var(--text-muted);
  font-size: 0.8rem;
  transition: all var(--transition);
  display: inline-block;
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
}
.footer-link::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 0;
  height: 1.5px;
  background: var(--nexcom-gold);
  transition: width var(--transition);
}
.footer-link:hover {
  color: var(--nexcom-gold);
  transform: translateX(4px);
}
.footer-link:hover::after { width: 100%; }
@media (max-width: 480px) {
  .footer-link { font-size: 0.75rem; }
}

.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.06);
  padding-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: var(--text-muted);
}
.footer-legal { display: flex; gap: 1.5rem; }
.footer-legal a { color: var(--text-muted); text-decoration: none; transition: color var(--transition); }
.footer-legal a:hover { color: var(--nexcom-gold); }
@media (max-width: 600px) {
  .footer-bottom { flex-direction: column; text-align: center; gap: 0.75rem; }
  .footer-legal { gap: 1rem; }
}
@media (max-width: 480px) {
  .footer-bottom { font-size: 0.65rem; }
}

/* ── Scroll to Top ── */
.scroll-top {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 500;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all var(--transition);
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px);
}
.scroll-top.visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}
.scroll-top:hover {
  transform: translateY(-3px);
}
@media (max-width: 480px) {
  .scroll-top { width: 34px; height: 34px; font-size: 0.9rem; bottom: 4.5rem; right: 1rem; }
}

/* ── Mobile Bottom Nav ── */
.mobile-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  background: #0a0a0a;
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 0.4rem 0;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.5);
}
@media (max-width: 768px) { .mobile-bottom-nav { display: flex; justify-content: space-around; } }

.mobile-bottom-nav button {
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--font-primary);
  font-size: 0.55rem;
  color: var(--text-muted);
  transition: all var(--transition);
  padding: 0.15rem 0.5rem;
  cursor: pointer;
  position: relative;
}
.mobile-bottom-nav button svg {
  font-size: 1.2rem;
  margin-bottom: 0.05rem;
}
.mobile-bottom-nav button.active {
  color: var(--nexcom-gold);
}
.mobile-bottom-nav button.active::after {
  content: '';
  position: absolute;
  top: -0.4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 3px;
  background: var(--nexcom-gold);
  border-radius: 4px;
}
.mobile-spacer { height: 3.5rem; }
@media (min-width: 769px) { .mobile-spacer { display: none; } }
@media (max-width: 480px) {
  .mobile-bottom-nav button { font-size: 0.5rem; }
  .mobile-bottom-nav button svg { font-size: 1rem; }
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0,0,0,0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}
.modal {
  background: #1a1a2e;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: var(--radius);
  max-width: 480px;
  width: 100%;
  padding: 1.5rem;
  box-shadow: var(--shadow-lg);
  animation: modalSlide 0.3s ease;
}
@keyframes modalSlide {
  from { opacity: 0; transform: scale(0.95) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.modal-header h2 { 
  font-family: var(--font-display);
  font-size: 1.3rem; 
  font-weight: 700; 
  color: var(--text-dark);
}
.modal-close {
  background: none;
  border: none;
  font-size: 1.3rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  transition: all var(--transition);
}
.modal-close:hover { color: var(--nexcom-gold); transform: rotate(90deg); }
.modal input,
.modal textarea {
  width: 100%;
  padding: 0.6rem 0.9rem;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 0.75rem;
  font-family: var(--font-body);
  font-size: 0.85rem;
  transition: all var(--transition);
  background: rgba(255,255,255,0.03);
  color: var(--text-dark);
  margin-bottom: 0.6rem;
}
.modal input:focus,
.modal textarea:focus {
  border-color: var(--nexcom-gold);
  outline: none;
  box-shadow: 0 0 0 3px rgba(249,115,22,0.05);
  background: rgba(255,255,255,0.05);
}
.modal input::placeholder,
.modal textarea::placeholder { color: var(--text-muted); }
.modal textarea { resize: vertical; min-height: 80px; }
.modal-submit {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--nexcom-gold);
  color: #fff;
  padding: 0.75rem;
  border-radius: 0.75rem;
  font-weight: 700;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
  font-family: var(--font-primary);
}
.modal-submit:hover {
  background: var(--nexcom-gold-dark);
  transform: translateY(-2px);
}
@media (max-width: 480px) {
  .modal { padding: 1.2rem; max-width: 100%; margin: 0.5rem; }
  .modal-header h2 { font-size: 1.1rem; }
  .modal input, .modal textarea { font-size: 0.8rem; padding: 0.5rem 0.7rem; }
  .modal-submit { font-size: 0.85rem; padding: 0.6rem; }
}

/* ── Form Feedback ── */
.form-success {
  background: rgba(20, 184, 166, 0.15);
  border: 1px solid var(--nexcom-teal);
  color: var(--nexcom-teal);
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
@media (max-width: 480px) {
  .form-success { font-size: 0.8rem; padding: 0.4rem 0.6rem; }
}

.form-error {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}
@media (max-width: 480px) {
  .form-error { font-size: 0.8rem; padding: 0.4rem 0.6rem; }
}

.modal-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.spinner {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}

/* ── Page Styles ── */
.page-container {
  min-height: 100vh;
  background: #0a0a0a;
}

.page-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.page-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.2rem;
  color: #a1a1aa;
  margin-bottom: 2rem;
}

/* ── Services Page ── */
.services-grid-page {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.service-card-page {
  background: rgba(30,30,40,0.6);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
  transition: all 0.3s ease;
}

.service-card-page:hover {
  transform: translateY(-5px);
  border-color: rgba(249,115,22,0.3);
  box-shadow: 0 10px 30px rgba(249,115,22,0.05);
}

.service-icon-page {
  font-size: 2.5rem;
  color: #f97316;
  margin-bottom: 1rem;
}

.service-card-page h2 {
  font-size: 1.5rem;
  color: #ffffff;
  margin-bottom: 1rem;
}

.service-card-page p {
  color: #a1a1aa;
  margin-bottom: 1rem;
  line-height: 1.6;
}

.service-card-page ul {
  list-style: none;
  padding: 0;
  margin-bottom: 1.5rem;
}

.service-card-page ul li {
  padding: 0.5rem 0;
  color: #a1a1aa;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.service-card-page ul li::before {
  content: "✓";
  color: #f97316;
  margin-right: 0.5rem;
  font-weight: bold;
}

.service-link {
  color: #f97316;
  text-decoration: none;
  font-weight: 600;
  display: inline-block;
  transition: all 0.3s ease;
}

.service-link:hover {
  transform: translateX(5px);
}

.service-cta {
  background: linear-gradient(135deg, #1a1a2e, #0a0a0a);
  border: 1px solid rgba(249,115,22,0.2);
  color: white;
  padding: 3rem 2rem;
  border-radius: 1rem;
  text-align: center;
  margin-top: 3rem;
}

.service-cta h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.cta-button-page {
  display: inline-block;
  padding: 0.75rem 2rem;
  background: #f97316;
  color: #fff;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.cta-button-page:hover {
  background: #ea580c;
  transform: translateY(-2px);
}

/* ── Support Page ── */
.support-grid-page {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.support-card-page {
  background: rgba(30,30,40,0.6);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
}

.support-card-page h2 {
  font-size: 1.5rem;
  color: #ffffff;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.contact-item:last-child {
  border-bottom: none;
}

.contact-icon {
  font-size: 1.5rem;
  color: #f97316;
  margin-top: 0.25rem;
}

.contact-item strong {
  display: block;
  color: #ffffff;
  margin-bottom: 0.25rem;
}

.contact-item p {
  color: #a1a1aa;
  margin: 0;
}

.contact-item a {
  color: #f97316;
  text-decoration: none;
}

.contact-item a:hover {
  text-decoration: underline;
}

.faq-item {
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.faq-item:last-child {
  border-bottom: none;
}

.faq-item h4 {
  color: #ffffff;
  margin-bottom: 0.5rem;
}

.faq-item p {
  color: #a1a1aa;
  margin: 0;
}

.faq-item a {
  color: #f97316;
  text-decoration: none;
}

.faq-item a:hover {
  text-decoration: underline;
}

.contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background: #f97316;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.contact-btn:hover {
  background: #ea580c;
  transform: translateY(-2px);
}

/* ── About Page ── */
.about-grid-page {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
}

.about-text h2 {
  color: #ffffff;
  margin: 1.5rem 0 1rem;
}

.about-text p {
  color: #a1a1aa;
  line-height: 1.8;
  margin-bottom: 1rem;
}

.about-features {
  list-style: none;
  padding: 0;
}

.about-features li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: #a1a1aa;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.about-features li svg {
  color: #f97316;
  font-size: 1.2rem;
}

.about-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.stat-card {
  background: rgba(30,30,40,0.6);
  padding: 1.5rem;
  border-radius: 1rem;
  text-align: center;
  border: 1px solid rgba(255,255,255,0.06);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #f97316;
}

.stat-label {
  color: #a1a1aa;
  margin-top: 0.5rem;
}

.cta-section-page {
  background: linear-gradient(135deg, #f97316, #ea580c);
  color: white;
  padding: 3rem 2rem;
  border-radius: 1rem;
  text-align: center;
  margin-top: 3rem;
}

.cta-section-page h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

/* ── Contact Page ── */
.contact-page-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 2rem;
  margin-top: 2rem;
}

.contact-info-card,
.contact-form-card {
  background: rgba(30,30,40,0.6);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
}

.contact-info-card h2,
.contact-form-card h2 {
  color: #ffffff;
  margin-bottom: 1.5rem;
}

.contact-detail {
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.contact-detail:last-child {
  border-bottom: none;
}

.contact-detail-icon {
  font-size: 1.5rem;
  color: #f97316;
  margin-top: 0.25rem;
}

.contact-detail strong {
  display: block;
  color: #ffffff;
}

.contact-detail p {
  color: #a1a1aa;
  margin: 0;
}

.contact-detail a {
  color: #f97316;
  text-decoration: none;
}

.social-links-page {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.social-links-page a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255,255,255,0.05);
  color: #a1a1aa;
  border-radius: 50%;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255,255,255,0.06);
}

.social-links-page a:hover {
  background: #f97316;
  color: white;
  transform: translateY(-2px);
}

.contact-form-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-form-page input,
.contact-form-page select,
.contact-form-page textarea {
  padding: 0.75rem;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255,255,255,0.03);
  color: #ffffff;
}

.contact-form-page input::placeholder,
.contact-form-page textarea::placeholder {
  color: #a1a1aa;
}

.contact-form-page input:focus,
.contact-form-page select:focus,
.contact-form-page textarea:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249,115,22,0.1);
}

.contact-form-page select option {
  background: #1a1a2e;
  color: #ffffff;
}

.contact-submit-btn {
  padding: 0.75rem;
  background: #f97316;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.contact-submit-btn:hover {
  background: #ea580c;
  transform: translateY(-2px);
}

/* ── Coverage Page ── */
.coverage-page-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
}

.coverage-map-placeholder {
  background: rgba(30,30,40,0.6);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coverage-info {
  text-align: center;
}

.coverage-icon-large {
  font-size: 4rem;
  color: #f97316;
  margin-bottom: 1rem;
}

.coverage-info h3 {
  color: #ffffff;
  margin-bottom: 1rem;
}

.coverage-info ul {
  list-style: none;
  padding: 0;
  columns: 2;
  text-align: left;
  color: #a1a1aa;
  margin: 1rem 0;
}

.coverage-info ul li {
  padding: 0.5rem 0;
}

.coverage-note {
  color: #a1a1aa;
  font-style: italic;
  margin-top: 1rem;
}

.coverage-actions-page {
  background: rgba(30,30,40,0.6);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.coverage-actions-page h3 {
  color: #ffffff;
  margin-bottom: 0.5rem;
}

.coverage-actions-page p {
  color: #a1a1aa;
  margin-bottom: 1.5rem;
}

.coverage-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.coverage-btn-primary,
.coverage-btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.coverage-btn-primary {
  background: #f97316;
  color: white;
}

.coverage-btn-primary:hover {
  background: #ea580c;
  transform: translateY(-2px);
}

.coverage-btn-secondary {
  background: rgba(255,255,255,0.05);
  color: #ffffff;
  border: 1px solid rgba(255,255,255,0.06);
}

.coverage-btn-secondary:hover {
  background: rgba(255,255,255,0.1);
  transform: translateY(-2px);
}

/* ── Careers Page ── */
.careers-intro {
  background: rgba(30,30,40,0.6);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
  margin-bottom: 2rem;
}

.careers-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(249,115,22,0.1);
  color: #f97316;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.9rem;
}

.careers-icon {
  font-size: 1.2rem;
}

.careers-values {
  display: flex;
  gap: 2rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.careers-values span {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #a1a1aa;
  font-weight: 500;
}

.careers-values svg {
  color: #f97316;
}

.job-listings h2 {
  color: #ffffff;
  margin-bottom: 1.5rem;
}

.job-card {
  background: rgba(30,30,40,0.6);
  padding: 1.5rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.job-card:hover {
  transform: translateY(-2px);
  border-color: rgba(249,115,22,0.3);
}

.job-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.job-header h3 {
  color: #ffffff;
}

.job-type {
  padding: 0.25rem 0.75rem;
  background: rgba(249,115,22,0.1);
  color: #f97316;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.job-meta {
  display: flex;
  gap: 1.5rem;
  color: #a1a1aa;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
}

.job-meta span {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.job-description {
  color: #a1a1aa;
  margin-bottom: 1rem;
}

.apply-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: #f97316;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-btn:hover {
  background: #ea580c;
  transform: translateY(-2px);
}

.careers-cta {
  background: linear-gradient(135deg, #1a1a2e, #0a0a0a);
  border: 1px solid rgba(249,115,22,0.2);
  color: white;
  padding: 3rem 2rem;
  border-radius: 1rem;
  text-align: center;
  margin-top: 2rem;
}

.careers-cta h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.careers-cta p {
  color: #a1a1aa;
  margin-bottom: 1.5rem;
}

/* ── Legal Page ── */
.legal-page .legal-content {
  background: rgba(30,30,40,0.6);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
}

.legal-content h3 {
  color: #ffffff;
  margin: 1.5rem 0 1rem;
}

.legal-content h3:first-child {
  margin-top: 0;
}

.legal-content p {
  color: #a1a1aa;
  line-height: 1.8;
  margin-bottom: 1rem;
}

.legal-content ul {
  color: #a1a1aa;
  line-height: 2;
  padding-left: 1.5rem;
}

.legal-date {
  margin-top: 2rem;
  color: #a1a1aa;
  font-style: italic;
}

.legal-nav {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 2rem;
  padding: 1.5rem;
  background: rgba(30,30,40,0.6);
  border-radius: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
}

.legal-nav a {
  color: #a1a1aa;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.legal-nav a:hover {
  color: #f97316;
}

/* ── Responsive Pages ── */
@media (max-width: 1024px) {
  .about-grid-page { grid-template-columns: 1fr; }
  .about-stats { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 768px) {
  .page-content { padding: 2rem 1rem; }
  .page-content h1 { font-size: 2rem; }
  .page-subtitle { font-size: 1rem; }
  .support-grid-page,
  .services-grid-page { grid-template-columns: 1fr; }
  .contact-page-grid { grid-template-columns: 1fr; }
  .coverage-page-content { grid-template-columns: 1fr; }
  .coverage-info ul { columns: 1; }
  .legal-nav { flex-direction: column; gap: 0.75rem; }
}

@media (max-width: 480px) {
  .page-content h1 { font-size: 1.6rem; }
  .stat-number { font-size: 2rem; }
  .careers-values { gap: 1rem; }
  .job-header { flex-direction: column; align-items: flex-start; gap: 0.5rem; }
  .job-meta { flex-direction: column; gap: 0.5rem; }
  .social-links-page a { width: 38px; height: 38px; font-size: 1rem; }
}

/* ── Device Page Styles ── */
.bg-surface-container-low {
  background-color: rgba(30, 30, 40, 0.4);
}

.bg-surface-container-high {
  background-color: rgba(30, 30, 40, 0.7);
}

.border-outline-variant\/10 {
  border-color: rgba(255, 255, 255, 0.06);
}

.border-outline-variant\/20 {
  border-color: rgba(255, 255, 255, 0.1);
}

.text-on-surface-variant {
  color: var(--text-muted);
}

.text-primary-dark {
  color: var(--text-dark);
}

.bg-primary-dark {
  background: var(--nexcom-gold-dark);
}

.bg-primary\/5 {
  background: rgba(249, 115, 22, 0.05);
}

.bg-primary\/10 {
  background: rgba(249, 115, 22, 0.1);
}

.bg-primary\/70 {
  background: rgba(249, 115, 22, 0.7);
}

.bg-primary\/80 {
  background: rgba(249, 115, 22, 0.8);
}

.bg-primary\/90 {
  background: rgba(249, 115, 22, 0.9);
}

.text-on-primary {
  color: #ffffff;
}

/* Glass card effect */
.glass-card {
  background: rgba(30, 30, 40, 0.6);
  backdrop-filter: blur(10px);
}

/* Device page specific */
.page-container .device-cta {
  margin-top: 3rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.page-container .device-cta p {
  color: var(--text-muted);
  font-size: 1rem;
}

.page-container .device-cta-link {
  color: var(--nexcom-gold);
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  font-family: var(--font-body);
  transition: color var(--transition);
}

.page-container .device-cta-link:hover {
  color: var(--nexcom-gold-dark);
}

/* Material Icons fallback */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-container .page-content .section-title {
    font-size: 2.5rem;
  }
  
  .page-container .page-content .section-title span {
    font-size: 2.5rem;
  }
}

@media (max-width: 480px) {
  .page-container .page-content .section-title {
    font-size: 2rem;
  }
  
  .page-container .page-content .section-title span {
    font-size: 2rem;
  }
}

/* ─── Banner Section - Clean Image Only ─── */
.banner-section {
  width: 100%;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.banner-container {
  position: relative;
  width: 100%;
  height: 450px;
  overflow: hidden;
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ─── Banner Responsive ─── */
@media (max-width: 1024px) {
  .banner-container {
    height: 380px;
  }
}

@media (max-width: 768px) {
  .banner-container {
    height: 300px;
  }
}

@media (max-width: 480px) {
  .banner-container {
    height: 220px;
  }
}

/* ─── Checkout Modal Styles ─── */
.checkout-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  padding: 0.5rem;
}

.checkout-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(4, 4, 4, 0.95);
  backdrop-filter: blur(12px);
  z-index: 9999;
  pointer-events: auto;
}

.checkout-modal {
  position: relative;
  background: #040404;
  width: 92%;
  max-width: 470px;
  max-height: 99vh;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  box-shadow: 0 32px 100px rgba(0, 0, 0, 0.7);
  overflow: hidden;
  pointer-events: auto;
  margin: auto;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 1.5rem;
}

/* ─── Modal Header ─── */
.checkout-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  background: rgba(4, 4, 4, 0.98);
  backdrop-filter: blur(10px);
}

.checkout-modal-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.checkout-modal-icon-wrapper {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(249, 115, 22, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.checkout-modal-icon {
  color: #f97316;
  font-size: 0.8rem;
}

.checkout-modal-header h2 {
  font-family: var(--font-display);
  font-size: 0.9rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.01em;
}

.checkout-modal-close {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: #a1a1aa;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.3rem;
  transition: all 0.3s ease;
  border-radius: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
}

.checkout-modal-close:hover {
  color: #ffffff;
  background: rgba(249, 115, 22, 0.15);
  border-color: rgba(249, 115, 22, 0.2);
  transform: rotate(90deg);
}

/* ─── Modal Body ─── */
.checkout-modal-body {
  flex: 1;
  padding: 0;
  overflow: hidden;
  background: #040404;
  position: relative;
  min-height: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkout-iframe {
  width: 100%;
  height: 100%;
  min-height: 780px;
  border: none;
  background: #040404;
  display: block;
  transition: opacity 0.4s ease;
}

/* ─── Loading State ─── */
.checkout-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #040404;
  z-index: 5;
}

.checkout-loader-spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(249, 115, 22, 0.1);
  border-top-color: #f97316;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.checkout-loader-text {
  margin-top: 0.75rem;
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #a1a1aa;
}

/* ─── Responsive ─── */
@media (max-width: 992px) {
  .checkout-modal {
    max-width: 440px;
    max-height: 98vh;
  }
  
  .checkout-iframe {
    min-height: 700px;
  }
}

@media (max-width: 768px) {
  .checkout-modal-wrapper {
    padding: 0;
  }

  .checkout-modal {
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
    height: 100vh;
  }

  .checkout-iframe {
    min-height: calc(100vh - 50px);
    height: calc(100vh - 50px);
  }

  .checkout-modal-header {
    padding: 0.4rem 0.85rem;
  }

  .checkout-modal-header h2 {
    font-size: 0.8rem;
  }

  .checkout-modal-icon-wrapper {
    width: 24px;
    height: 24px;
  }

  .checkout-modal-icon {
    font-size: 0.7rem;
  }

  .checkout-modal-overlay {
    background: rgba(4, 4, 4, 0.98);
  }

  .checkout-modal-close {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }

  .checkout-loader-text {
    font-size: 0.7rem;
  }
  
  .checkout-loader-spinner {
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 480px) {
  .checkout-iframe {
    min-height: calc(100vh - 45px);
    height: calc(100vh - 45px);
  }

  .checkout-modal-header {
    padding: 0.35rem 0.65rem;
  }

  .checkout-modal-header h2 {
    font-size: 0.7rem;
  }

  .checkout-modal-icon-wrapper {
    width: 20px;
    height: 20px;
  }

  .checkout-modal-icon {
    font-size: 0.6rem;
  }

  .checkout-modal-close {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
    padding: 0.2rem;
  }

  .checkout-loader-spinner {
    width: 24px;
    height: 24px;
  }

  .checkout-loader-text {
    font-size: 0.6rem;
  }
}

/* ─── Page Background for OL Checkout ─── */
.ol-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  background: #040404;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', 'DM Sans', system-ui, sans-serif;
}

/* ─── Hide scrollbar on iframe container ─── */
.checkout-modal-body::-webkit-scrollbar {
  display: none;
}

.checkout-modal-body {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.checkout-iframe::-webkit-scrollbar {
  display: none;
}

.checkout-iframe {
  -ms-overflow-style: none;
  scrollbar-width: none;
}